import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Brain, LayoutDashboard, LogOut, BookOpen, BarChart3, Store, Activity, Users, Settings, Crown, Tag, ShieldCheck } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.plan === 'pro' || user?.subscription?.plan === 'enterprise';

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Prototype disclaimer — very subtle top strip */}
      <div
        className="bg-[#09090B] text-white/70 text-center py-1.5 border-b border-zinc-800"
        data-testid="prototype-disclaimer"
      >
        <p className="mono-data text-[9px] sm:text-[10px] tracking-[0.15em] leading-tight px-3">
          PROTOTYPE · Not for commercial or therapeutic use · Educational preview only
        </p>
      </div>
      <nav
        className="sticky top-0 z-50 bg-white border-b border-[#09090B]"
        data-testid="navbar"
      >
      <div className="container-swiss flex items-center h-14 gap-2">
        <Link
          to="/"
          className="flex items-center gap-2 group flex-shrink-0"
          data-testid="nav-logo"
        >
          <Brain className="w-5 h-5 text-[#09090B]" />
          <span className="font-bold text-sm tracking-widest uppercase" style={{ fontFamily: 'Outfit, sans-serif' }}>
            FOKYES
          </span>
        </Link>

        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 justify-end">
          <Link
            to="/programs"
            className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            data-testid="nav-programs"
          >
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Programs</span>
          </Link>
          <Link
            to="/cognoguru"
            className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            data-testid="nav-cognoguru"
          >
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">CognoGuru</span>
          </Link>
          <Link
            to="/marketplace"
            className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            data-testid="nav-marketplace"
          >
            <Store className="w-4 h-4" />
            <span className="hidden sm:inline">Marketplace</span>
          </Link>
          <Link
            to="/pricing"
            className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            data-testid="nav-pricing"
          >
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Pricing</span>
          </Link>

          {user ? (
            <>
              <Link
                to="/dashboard"
                className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
              <Link
                to="/profile"
                className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                data-testid="nav-profile"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Profile</span>
              </Link>
              <Link
                to="/brainmap"
                className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                data-testid="nav-brainmap"
              >
                <Activity className="w-4 h-4" />
                <span className="hidden sm:inline">BrainMap</span>
              </Link>
              <Link
                to="/parent"
                className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                data-testid="nav-parent"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Parent</span>
              </Link>
              <Link
                to="/settings"
                className="px-3 py-2 text-sm font-medium hover:bg-zinc-100 transition-colors flex-shrink-0"
                data-testid="nav-settings"
                aria-label="Settings"
              >
                <Settings className="w-4 h-4" />
              </Link>

              {!isPro && (
                <Link
                  to="/pricing"
                  className="ml-1 bg-[#09090B] text-white px-4 py-2 text-xs font-bold tracking-widest uppercase hover:bg-zinc-800 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                  data-testid="nav-upgrade-pro"
                >
                  <Crown className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Upgrade</span>
                </Link>
              )}
              {isPro && (
                <span className="ml-1 border border-[#09090B] px-3 py-1.5 text-xs font-bold tracking-widest uppercase flex items-center gap-1.5 flex-shrink-0" data-testid="nav-pro-badge">
                  <Crown className="w-3.5 h-3.5" /> Pro
                </span>
              )}

              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium tracking-wide uppercase hover:bg-zinc-100 transition-colors flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          ) : (
            <button
              onClick={handleLogin}
              className="btn-primary text-xs px-6 py-2"
              data-testid="nav-login"
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    </nav>
    </>
  );
}
